/**
 * Cognitive State — minimal developer panel.
 *
 * Per CTO Directive (post-#82/#83): NO design polish, NO redesign.
 * This is a small internal tool to inspect and set ME's current
 * cognitive context (mode + setting + listeners + runtime).
 *
 * Read it as: "What is ME thinking with, right now, for me?"
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

async function getJSON(path) {
  const r = await api.get(path);
  return r.data;
}

async function postJSON(path, body) {
  const r = await api.post(path, body);
  return r.data;
}

export default function CognitiveStatePage() {
  const [catalog, setCatalog] = useState(null);
  const [state, setState] = useState(null);
  const [persona, setPersona] = useState(null);
  const [utterance, setUtterance] = useState("");
  const [inferResult, setInferResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [c, s, p] = await Promise.all([
        getJSON("/context/catalog?language=tr"),
        getJSON("/context/current"),
        getJSON("/brain/persona?language=tr"),
      ]);
      setCatalog(c);
      setState(s);
      setPersona(p);
      setError(null);
    } catch (e) {
      setError(String(e));
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const apply = async (patch) => {
    setBusy(true);
    try {
      const next = await postJSON("/context/set", patch);
      setState(next);
      const p = await getJSON("/brain/persona?language=tr");
      setPersona(p);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleListener = (key) => {
    if (!state) return;
    const has = state.listeners?.includes(key);
    const next = has
      ? state.listeners.filter((l) => l !== key)
      : [...(state.listeners || []), key];
    apply({ listeners: next.length ? next : ["self"] });
  };

  const runInfer = async () => {
    if (!utterance.trim()) return;
    setBusy(true);
    try {
      const r = await postJSON("/context/infer", {
        utterance,
        apply_if_confident: false,
        threshold: 0.5,
      });
      setInferResult(r);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  const applyInferred = async () => {
    if (!inferResult?.suggestion) return;
    const s = inferResult.suggestion;
    const patch = {};
    if (s.suggested_mode) patch.mode = s.suggested_mode;
    if (s.suggested_setting) patch.setting = s.suggested_setting;
    if (Object.keys(patch).length) {
      await apply(patch);
      setInferResult(null);
      setUtterance("");
    }
  };

  const wrap = {
    minHeight: "100vh",
    background: "#0a0f1c",
    color: "#cfd8ea",
    padding: "32px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "13px",
    lineHeight: 1.55,
  };
  const card = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
  };
  const h = { fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#7d8db0", marginBottom: "8px" };
  const pill = (active) => ({
    display: "inline-block",
    padding: "4px 10px",
    margin: "2px 4px 2px 0",
    borderRadius: "999px",
    border: active ? "1px solid #60a5fa" : "1px solid rgba(255,255,255,0.12)",
    background: active ? "rgba(96,165,250,0.15)" : "transparent",
    color: active ? "#bfdbfe" : "#cfd8ea",
    cursor: "pointer",
    fontSize: "12px",
  });
  const input = {
    width: "100%",
    background: "rgba(0,0,0,0.3)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: "6px",
    padding: "8px 10px",
    color: "#cfd8ea",
    fontFamily: "inherit",
    fontSize: "13px",
  };
  const btn = {
    background: "rgba(96,165,250,0.18)",
    border: "1px solid rgba(96,165,250,0.45)",
    color: "#dbeafe",
    padding: "6px 14px",
    borderRadius: "6px",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "12px",
    marginRight: "8px",
  };

  if (!catalog || !state) {
    return <div style={wrap} data-testid="cognitive-state-loading">Yükleniyor…</div>;
  }

  return (
    <div style={wrap} data-testid="cognitive-state-page">
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#7d8db0", letterSpacing: "0.18em", textTransform: "uppercase" }}>
          ME · Cognitive State (developer)
        </div>
        <h1 style={{ fontSize: 22, margin: "4px 0", color: "#e6edf7", fontFamily: "ui-sans-serif, system-ui" }}>
          ME şu an nasıl düşünüyor?
        </h1>
        <div style={{ color: "#7d8db0" }}>
          Bu panel yalnızca iç geliştirme/teşhis amaçlıdır. ME&apos;nin kimliği aynı kalır;
          değişen yalnızca bilişsel mod ve bağlamdır.
        </div>
      </div>

      {error && (
        <div style={{ ...card, borderColor: "#b45353", color: "#fca5a5" }} data-testid="cognitive-state-error">
          {error}
        </div>
      )}

      <div style={card} data-testid="cognitive-state-snapshot">
        <div style={h}>Mevcut durum</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          <div>
            <div style={{ color: "#7d8db0", fontSize: 11 }}>Mode</div>
            <div style={{ color: "#e6edf7", fontSize: 15 }} data-testid="cognitive-state-mode">{state.mode}</div>
          </div>
          <div>
            <div style={{ color: "#7d8db0", fontSize: 11 }}>Setting</div>
            <div style={{ color: "#e6edf7", fontSize: 15 }} data-testid="cognitive-state-setting">{state.setting}</div>
          </div>
          <div>
            <div style={{ color: "#7d8db0", fontSize: 11 }}>Listeners</div>
            <div style={{ color: "#e6edf7", fontSize: 15 }} data-testid="cognitive-state-listeners">
              {(state.listeners || []).join(", ")}
            </div>
          </div>
          <div>
            <div style={{ color: "#7d8db0", fontSize: 11 }}>Runtime</div>
            <div style={{ color: "#e6edf7", fontSize: 15 }} data-testid="cognitive-state-runtime">{state.runtime}</div>
          </div>
        </div>
        {persona?.current_audience && (
          <div style={{ marginTop: 12, color: "#7d8db0" }}>
            Audience overlay (Persona Bible) →{" "}
            <span style={{ color: "#bfdbfe" }} data-testid="cognitive-state-audience">{persona.current_audience}</span>
          </div>
        )}
      </div>

      <div style={card}>
        <div style={h}>Cognitive Mode ({catalog.modes.length})</div>
        <div data-testid="cognitive-state-modes">
          {catalog.modes.map((m) => (
            <span
              key={m.id}
              style={pill(state.mode === m.id)}
              onClick={() => !busy && apply({ mode: m.id })}
              data-testid={`mode-pill-${m.id}`}
              title={m.thinking}
            >
              {m.label}
            </span>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={h}>Setting</div>
        <div data-testid="cognitive-state-settings">
          {catalog.settings.map((s) => (
            <span
              key={s}
              style={pill(state.setting === s)}
              onClick={() => !busy && apply({ setting: s })}
              data-testid={`setting-pill-${s}`}
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={h}>Listeners (multi-select)</div>
        <div>
          {catalog.listeners.map((l) => (
            <span
              key={l}
              style={pill(state.listeners?.includes(l))}
              onClick={() => !busy && toggleListener(l)}
              data-testid={`listener-pill-${l}`}
            >
              {l}
            </span>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={h}>Runtime</div>
        <div>
          {catalog.runtimes.map((r) => (
            <span
              key={r}
              style={pill(state.runtime === r)}
              onClick={() => !busy && apply({ runtime: r })}
              data-testid={`runtime-pill-${r}`}
            >
              {r}
            </span>
          ))}
        </div>
      </div>

      <div style={card}>
        <div style={h}>Inference (sezgisel — yalnızca öneri)</div>
        <input
          style={input}
          value={utterance}
          onChange={(e) => setUtterance(e.target.value)}
          placeholder="Örn: Hastanın HbA1c laboratuvar sonuçlarına bakalım"
          data-testid="cognitive-state-infer-input"
        />
        <div style={{ marginTop: 8 }}>
          <button style={btn} onClick={runInfer} disabled={busy} data-testid="cognitive-state-infer-button">
            Çıkar
          </button>
          {inferResult?.suggestion?.suggested_mode || inferResult?.suggestion?.suggested_setting ? (
            <button style={btn} onClick={applyInferred} disabled={busy} data-testid="cognitive-state-apply-inferred">
              Öneriyi uygula
            </button>
          ) : null}
        </div>
        {inferResult && (
          <pre style={{ marginTop: 10, color: "#9aa6c0", fontSize: 12 }} data-testid="cognitive-state-infer-result">
            {JSON.stringify(inferResult.suggestion, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}
